import React from 'react';

/**
 * Inline stylesheet that styles `.article-content` (markdown-rendered HTML)
 * and a handful of decorative blocks (`.equipment-spotlight`, `.pro-tip`,
 * `.training-phase`, `.weekly-schedule`, `.comparison-table`, `.faq-item`,
 * `.lead-capture-inline`, `.sticky-cta-bar`, `.toc-container`).
 *
 * Lifted out of ArticlePage.jsx to keep page logic readable.
 */
export const ArticleStyles = () => (
  <style>{`
    .article-content {
      font-family: 'Manrope', sans-serif;
      color: #a1a1aa;
      line-height: 1.8;
    }
    .article-content h2 {
      font-family: 'Bebas Neue', sans-serif;
      font-size: 2rem;
      color: #ffffff;
      margin-top: 3rem;
      margin-bottom: 1.5rem;
      letter-spacing: 0.025em;
    }
    .article-content h3 {
      font-family: 'Bebas Neue', sans-serif;
      font-size: 1.5rem;
      color: #e4e4e7;
      margin-top: 2rem;
      margin-bottom: 1rem;
    }
    .article-content h4 {
      color: #ffffff;
      font-size: 1.125rem;
      font-weight: 600;
      margin-top: 1.5rem;
      margin-bottom: 0.75rem;
    }
    .article-content h5 {
      color: #d4d4d8;
      font-size: 0.95rem;
      font-weight: 600;
      margin-top: 1rem;
      margin-bottom: 0.5rem;
    }
    .article-content .article-h2 {
      font-family: 'Bebas Neue', sans-serif;
      font-size: 2rem;
      color: #ffffff;
      margin-top: 3rem;
      margin-bottom: 1.5rem;
      letter-spacing: 0.025em;
    }
    .article-content .article-h3 {
      font-family: 'Bebas Neue', sans-serif;
      font-size: 1.5rem;
      color: #e4e4e7;
      margin-top: 2rem;
      margin-bottom: 1rem;
    }
    .article-content p, .article-content .article-p {
      margin-bottom: 1.25rem;
      color: #a1a1aa;
    }
    .article-content strong { color: #ffffff; font-weight: 600; }
    .article-content em { color: #d4d4d8; }
    .article-content .article-quote, .article-content blockquote {
      border-left: 3px solid #E63946;
      padding-left: 1.5rem;
      margin: 2rem 0;
      color: #d4d4d8;
      font-style: italic;
      font-size: 1.125rem;
    }
    .article-content ul, .article-content .article-ul {
      list-style: none;
      padding-left: 0;
      margin: 1.5rem 0;
    }
    .article-content li, .article-content .article-li {
      position: relative;
      padding-left: 1.5rem;
      margin-bottom: 0.75rem;
      color: #a1a1aa;
    }
    .article-content li::before, .article-content .article-li::before {
      content: '\\2192';
      position: absolute;
      left: 0;
      color: #E63946;
    }
    .article-content ol {
      list-style: none;
      counter-reset: ol-counter;
      padding-left: 0;
      margin: 1.5rem 0;
    }
    .article-content ol li { counter-increment: ol-counter; padding-left: 2rem; }
    .article-content ol li::before {
      content: counter(ol-counter) '.';
      color: #FFD100;
      font-weight: 700;
      font-family: 'Bebas Neue', sans-serif;
      font-size: 1.1rem;
    }
    .article-content a { color: #00F0FF; text-decoration: none; transition: color 0.2s; }
    .article-content a:hover { color: #ffffff; text-decoration: underline; }
    .article-content img, .article-content .article-img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 2rem 0;
      border: 1px solid #27272a;
    }

    /* Equipment Spotlight */
    .article-content .equipment-spotlight {
      background: linear-gradient(135deg, #1a1a1a 0%, #27272a 100%);
      border-left: 4px solid #FFD100;
      border-radius: 8px;
      padding: 28px;
      margin: 2.5rem 0;
      position: relative;
      overflow: hidden;
    }
    .article-content .equipment-spotlight::before {
      content: "GEAR UP";
      position: absolute;
      top: 0;
      right: 0;
      background: #FFD100;
      color: #0a0a0a;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 1px;
      padding: 6px 14px;
      border-radius: 0 8px 0 8px;
      font-family: 'Bebas Neue', sans-serif;
    }
    .article-content .equipment-spotlight h4 { color: #fff; font-size: 1.2rem; margin: 0 0 12px 0; font-weight: 600; }
    .article-content .equipment-spotlight p { color: #d4d4d8; font-size: 0.95rem; line-height: 1.7; margin: 0 0 16px 0; }
    .article-content .equipment-spotlight .cta-button {
      display: inline-block;
      background: #FFD100;
      color: #0a0a0a;
      font-weight: 700;
      font-size: 14px;
      text-decoration: none !important;
      padding: 12px 24px;
      border-radius: 6px;
      transition: all 0.2s ease;
      font-family: 'Bebas Neue', sans-serif;
      letter-spacing: 0.5px;
    }
    .article-content .equipment-spotlight .cta-button:hover {
      background: #ffe44d;
      color: #0a0a0a !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(255, 209, 0, 0.3);
      text-decoration: none !important;
    }
    .article-content .equipment-spotlight .discount-code {
      display: inline-block;
      background: rgba(255, 209, 0, 0.12);
      border: 1px dashed #FFD100;
      color: #FFD100;
      font-family: monospace;
      font-size: 13px;
      font-weight: 600;
      padding: 8px 16px;
      border-radius: 4px;
      margin-left: 12px;
    }

    /* Pro Tip */
    .article-content .pro-tip {
      background: rgba(255, 209, 0, 0.06);
      border: 1px solid rgba(255, 209, 0, 0.25);
      border-radius: 8px;
      padding: 20px 24px;
      margin: 2rem 0;
    }
    .article-content .pro-tip::before {
      content: "PRO TIP";
      display: block;
      color: #FFD100;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 1px;
      margin-bottom: 8px;
      font-family: 'Bebas Neue', sans-serif;
    }
    .article-content .pro-tip p { color: #d4d4d8; font-size: 0.95rem; line-height: 1.7; margin: 0; }

    /* Training Phase Cards */
    .article-content .training-phase {
      background: #18181b;
      border: 1px solid #27272a;
      border-radius: 8px;
      padding: 24px;
      margin: 1.5rem 0;
    }
    .article-content .training-phase .phase-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
    .article-content .training-phase .phase-number {
      background: #FFD100;
      color: #0a0a0a;
      font-weight: 700;
      font-size: 14px;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-family: 'Bebas Neue', sans-serif;
      font-size: 1.1rem;
    }
    .article-content .training-phase .phase-title {
      color: #fff;
      font-size: 1.2rem;
      font-weight: 600;
      margin: 0;
      font-family: 'Bebas Neue', sans-serif;
      letter-spacing: 0.5px;
    }
    .article-content .training-phase .phase-weeks { color: #FFD100; font-size: 13px; font-weight: 500; }
    .article-content .training-phase .phase-workout {
      background: #0f0f0f;
      border: 1px solid #27272a;
      border-radius: 6px;
      padding: 16px 20px;
      margin-top: 16px;
    }
    .article-content .training-phase .phase-workout h5 {
      color: #FFD100;
      font-family: 'Bebas Neue', sans-serif;
      font-size: 1rem;
      margin: 0 0 12px 0;
      letter-spacing: 0.5px;
    }
    .article-content .training-phase .phase-workout ul { margin: 0; }
    .article-content .training-phase .phase-workout li { margin-bottom: 0.5rem; font-size: 0.9rem; }

    /* Weekly Schedule */
    .article-content .weekly-schedule { display: grid; gap: 8px; margin: 1.5rem 0; }
    .article-content .schedule-day {
      display: grid;
      grid-template-columns: 100px 160px 1fr;
      gap: 12px;
      align-items: center;
      padding: 14px 16px;
      background: #18181b;
      border: 1px solid #27272a;
      border-radius: 6px;
      transition: border-color 0.2s;
    }
    .article-content .schedule-day:hover { border-color: #FFD100; }
    .article-content .schedule-day .day-name {
      color: #FFD100;
      font-family: 'Bebas Neue', sans-serif;
      font-size: 1rem;
      letter-spacing: 0.5px;
    }
    .article-content .schedule-day .day-type { color: #ffffff; font-weight: 600; font-size: 0.9rem; }
    .article-content .schedule-day .day-desc { color: #71717a; font-size: 0.85rem; }

    /* Comparison Table */
    .article-content .comparison-table, .article-content table {
      width: 100%;
      border-collapse: collapse;
      margin: 2rem 0;
      font-size: 0.9rem;
      border: 1px solid #27272a;
      border-radius: 8px;
      overflow: hidden;
    }
    .article-content table th {
      background: #FFD100;
      color: #0a0a0a;
      font-family: 'Bebas Neue', sans-serif;
      font-size: 1rem;
      letter-spacing: 0.5px;
      padding: 14px 16px;
      text-align: left;
      font-weight: 600;
    }
    .article-content table td {
      padding: 14px 16px;
      border-bottom: 1px solid #27272a;
      color: #d4d4d8;
      background: #18181b;
    }
    .article-content table tr:hover td { background: #1f1f22; }

    /* FAQ Section */
    .article-content .faq-section { margin: 2rem 0; }
    .article-content .faq-item {
      border: 1px solid #27272a;
      border-radius: 8px;
      margin-bottom: 12px;
      overflow: hidden;
    }
    .article-content .faq-item .faq-question {
      background: #18181b;
      color: #ffffff;
      padding: 18px 20px;
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      cursor: default;
    }
    .article-content .faq-item .faq-answer {
      padding: 0 20px 18px 20px;
      margin: 0;
      color: #a1a1aa;
      font-size: 0.9rem;
      line-height: 1.7;
      background: #18181b;
    }

    /* Inline Lead Capture */
    .article-content .lead-capture-inline {
      background: #18181b;
      border: 1px solid #27272a;
      border-radius: 12px;
      padding: 36px;
      margin: 2.5rem 0;
      text-align: center;
    }
    .article-content .lead-capture-inline .lead-icon { font-size: 48px; margin-bottom: 12px; }
    .article-content .lead-capture-inline h3 {
      color: #fff;
      font-size: 1.4rem;
      margin: 0 0 8px 0;
      font-family: 'Bebas Neue', sans-serif;
      letter-spacing: 0.5px;
    }
    .article-content .lead-capture-inline .lead-subtitle { color: #71717a; font-size: 0.9rem; margin: 0 0 20px 0; }
    .article-content .lead-capture-inline .lead-benefits {
      display: flex;
      justify-content: center;
      gap: 24px;
      flex-wrap: wrap;
      margin-bottom: 24px;
    }
    .article-content .lead-capture-inline .benefit-item {
      color: #d4d4d8;
      font-size: 0.85rem;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .article-content .lead-capture-inline .benefit-item::before {
      content: "\\2713";
      color: #FFD100;
      font-weight: 700;
    }
    .article-content .lead-capture-inline .lead-capture-cta {
      display: inline-block;
      background: #FFD100;
      color: #0a0a0a;
      font-weight: 700;
      font-size: 15px;
      border: none;
      padding: 14px 32px;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: 'Bebas Neue', sans-serif;
      letter-spacing: 0.5px;
    }
    .article-content .lead-capture-inline .lead-capture-cta:hover {
      background: #ffe44d;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(255, 209, 0, 0.3);
    }

    /* Sticky CTA Bar */
    .sticky-cta-bar {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: #0a0a0a;
      border-top: 1px solid #27272a;
      padding: 12px 20px;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 16px;
      z-index: 50;
      transform: translateY(100%);
      transition: transform 0.3s ease;
    }
    .sticky-cta-bar.visible { transform: translateY(0); }

    @media (max-width: 768px) {
      .article-content .equipment-spotlight { padding: 20px 16px; padding-top: 44px; }
      .article-content .equipment-spotlight .discount-code { display: block; margin: 12px 0 0 0; }
      .article-content .lead-capture-inline .lead-benefits { flex-direction: column; gap: 8px; align-items: center; }
      .article-content .schedule-day { grid-template-columns: 1fr; gap: 4px; }
      .article-content table { font-size: 0.8rem; }
      .article-content table th, .article-content table td { padding: 10px 12px; }
    }

    /* Table of Contents */
    .toc-container {
      padding: 1.25rem 1.5rem;
      background: #111113;
      border: 1px solid #27272a;
      border-radius: 2px;
    }
    .toc-list { list-style: none; padding: 0; margin: 0; }
    .toc-item { margin: 0; }
    .toc-item.toc-sub { padding-left: 1rem; }
    .toc-link {
      display: block;
      width: 100%;
      text-align: left;
      padding: 6px 10px;
      font-size: 0.8rem;
      line-height: 1.4;
      color: #71717a;
      background: none;
      border: none;
      border-left: 2px solid transparent;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: 'Manrope', sans-serif;
    }
    .toc-link:hover { color: #d4d4d8; border-left-color: #3f3f46; }
    .toc-link.toc-active {
      color: #E63946;
      border-left-color: #E63946;
      background: rgba(230, 57, 70, 0.05);
    }
    .toc-item.toc-sub .toc-link { font-size: 0.75rem; padding: 4px 10px; color: #52525b; }
    .toc-item.toc-sub .toc-link:hover { color: #a1a1aa; }
    .toc-item.toc-sub .toc-link.toc-active { color: #E63946; }
    .scrollbar-thin::-webkit-scrollbar { width: 4px; }
    .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
    .scrollbar-thin::-webkit-scrollbar-thumb { background: #27272a; border-radius: 2px; }

    /* Article table wrapper for overflow */
    .article-table-wrap { overflow-x: auto; margin: 2rem 0; }

    @media (max-width: 1024px) {
      .toc-container { display: none; }
    }
  `}</style>
);
